import { useEffect } from "react";
import { logEvent } from "../../../infra/firebase/analytics";
import { AnalyticsEvent, AnalyticsParam } from "../../../domain/analytics";
import { PageMeta } from "../../seo/PageMeta";
import styles from "./TermsOfServicePage.module.css";

export default function TermsOfServicePage() {
  useEffect(() => {
    logEvent(AnalyticsEvent.screenView, {
      [AnalyticsParam.screenName]: "terms_of_service",
    });
  }, []);

  return (
    <div className={styles.page}>
      <PageMeta
        title="Terms of Service"
        description="Breath Lab's terms of service — the rules and conditions for using the app."
        path="/terms-of-service"
      />
      <div className={styles.content}>
        <h1 className={styles.title}>Terms of Service</h1>
        <p className={styles.lastUpdated}>Last updated: May 14, 2026</p>

        <p className={styles.intro}>
          Please read these Terms of Service (&ldquo;Terms&rdquo;) carefully
          before using the Breath Lab mobile application and website (the
          &ldquo;Service&rdquo;) operated by Sequence Studios Ltd. (Company
          No. 15455683), a company registered in the United Kingdom
          (&ldquo;Sequence Studios&rdquo;, &ldquo;we&rdquo;, &ldquo;us&rdquo;,
          or &ldquo;our&rdquo;). By accessing or using the Service, you agree to
          be bound by these Terms.
        </p>

        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>1. Acceptance of Terms</h2>
          <p>
            By creating an account or using Breath Lab, you confirm that you are
            at least 13 years of age and agree to these Terms and our Privacy
            Policy. If you do not agree, please do not use the Service.
          </p>
        </section>

        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>2. Description of Service</h2>
          <p>
            Breath Lab is a pranayama and guided breathwork application,
            available as a mobile app (iOS and Android) and a web app. The
            Service provides traditional pranayama techniques with configurable
            session parameters, multi-day journeys built around specific goals
            (such as sleep, stress, focus, or energy), mood logging before and
            after sessions, achievements and streaks, and educational content
            covering technique guides, cautions, and references to relevant
            scientific literature. The Service is provided for informational
            and wellness purposes only and does not constitute medical advice.
          </p>
        </section>

        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>
            3. Health Disclaimer and Assumption of Risk
          </h2>
          <p>
            <strong>Important:</strong> Breath Lab is not a medical application.
            The content provided is for general wellness and educational
            purposes only and is not a substitute for professional medical
            advice, diagnosis, or treatment. Nothing in the Service should be
            relied upon as medical guidance.
          </p>
          <p>
            Breathwork and breathing exercises carry inherent physical risks.
            Some techniques — including those involving rapid or controlled
            breathing — can cause dizziness, light-headedness, tingling, altered
            states of consciousness, or temporary loss of consciousness. Certain
            techniques may be unsuitable or dangerous for individuals with
            specific health conditions.
          </p>
          <p>
            <strong>Always consult a qualified healthcare professional</strong>{" "}
            before starting any new breathing practice, particularly if you have
            a pre-existing medical condition, are pregnant, have a history of
            cardiovascular or respiratory disease, epilepsy, or any condition
            affected by changes in blood oxygen or carbon dioxide levels.
          </p>
          <p>
            Never practise breathing exercises while driving, operating
            machinery, in or near water, or in any situation where losing
            consciousness could be dangerous.
          </p>
          <p>
            <strong>
              By using the Service, you acknowledge and accept these risks.
            </strong>{" "}
            You agree to use the Service at your own risk and that Sequence
            Studios shall not be liable for any physical injury, illness, loss
            of consciousness, or other harm arising from or in connection with
            your use of the Service or the practice of any breathing techniques
            described within it. Nothing in this section limits liability for
            death or personal injury caused by our negligence, as set out in
            Section 9.
          </p>
        </section>

        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>4. User Accounts</h2>
          <p>
            You are responsible for maintaining the confidentiality of your
            account credentials and for all activity that occurs under your
            account. Notify us promptly at{" "}
            <a href="mailto:info@sequence-studios.com">
              info@sequence-studios.com
            </a>{" "}
            if you suspect any unauthorised use of your account.
          </p>
          <p>
            We reserve the right to suspend or terminate accounts that violate
            these Terms or that we reasonably believe are being used
            fraudulently.
          </p>
        </section>

        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>5. Subscriptions and Payments</h2>
          <p>
            Breath Lab offers a free tier and a premium subscription
            (&ldquo;Breath Lab Premium&rdquo;). Premium features are available
            through the following channels:
          </p>
          <ul className={styles.list}>
            <li>
              <strong>Mobile (iOS):</strong> via in-app purchase through the
              Apple App Store. Subscriptions are managed through your Apple ID
              account settings. Refunds are subject to Apple&rsquo;s refund
              policy.
            </li>
            <li>
              <strong>Mobile (Android):</strong> via in-app purchase through
              Google Play. Subscriptions are managed through your Google Play
              account settings. Refunds are subject to Google&rsquo;s refund
              policy.
            </li>
            <li>
              <strong>Web:</strong> via direct payment processed by RevenueCat
              and Stripe. Billing terms and cancellation options will be
              presented at the point of purchase.
            </li>
          </ul>
          <p>
            <strong>Free trial (web).</strong> New web users are offered a
            7-day free trial of Breath Lab Premium. No payment details are
            collected to start the trial, and you will not be charged during
            the trial period. You will only be billed if you choose to
            subscribe at or after the trial ends, at which point the standard
            subscription pricing applies.
          </p>
          <p>
            <strong>Free trial (mobile).</strong> On iOS and Android, a free
            trial of Breath Lab Premium is available only on the annual
            subscription plan and starts at the moment you subscribe through
            the App Store or Google Play. Because the trial is operated as an
            introductory offer through those stores, a valid payment method on
            your Apple ID or Google account is required to start it, and the
            annual subscription will renew automatically at the end of the
            trial unless you cancel through the relevant store before the
            renewal date. The length of the trial, eligible plans, and the
            auto-renewal terms are governed by the store&rsquo;s own
            introductory-offer rules in addition to these Terms.
          </p>
          <p>
            <strong>Auto-renewal.</strong> Once you start a paid subscription,
            it automatically renews at the end of each billing period unless
            cancelled before the renewal date. For mobile subscriptions,
            cancellation must be made at least 24 hours before the end of the
            current billing period. For web subscriptions, cancellation takes
            effect at the end of the current billing period and you retain
            access to premium features until that date.
          </p>
          <p>
            <strong>Web subscription cooling-off period.</strong> Under the UK
            Consumer Contracts (Information, Cancellation and Additional
            Charges) Regulations 2013, and equivalent consumer-protection law
            in the European Union (Consumer Rights Directive 2011/83/EU), you
            have the right to cancel a paid web subscription within 14 days of
            being first charged without giving any reason (the
            &ldquo;cooling-off period&rdquo;). Where you started with a free
            trial, the 14-day cooling-off period begins on the day you are
            first charged, not on the day the trial began. However, if you
            continue to use the premium features after first being charged,
            you expressly request that we begin providing the paid Service
            immediately and acknowledge that your right to cancel will be lost
            once the Service has been fully performed. To exercise your
            cooling-off right, cancel your subscription via the Profile page
            and then contact us at{" "}
            <a href="mailto:info@sequence-studios.com">
              info@sequence-studios.com
            </a>{" "}
            within 14 days of being first charged to request a refund. We will
            process a full refund within 14 days of receiving your request.
          </p>
          <p>
            We reserve the right to change subscription pricing. We will notify
            active subscribers of any price increases by email or in-app
            notification before the new price takes effect.
          </p>
          <p>
            By purchasing a subscription, you represent that you are at least 18
            years of age, or that you have obtained the consent of a parent or
            legal guardian.
          </p>
          <p>
            If your subscription lapses or is cancelled, your access to premium
            features will end at the close of the paid period. Your account and
            free-tier access will remain intact.
          </p>
        </section>

        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>6. Beta Access — Web App</h2>
          <p>
            The Breath Lab web application is currently in beta. During this
            period, we are still actively testing and improving the web app to
            ensure stability and reliability, and working toward feature parity
            between the web app and the mobile app. The set of features
            available on each platform may therefore differ from time to time.
          </p>
          <p>
            Beta status does not affect pricing or your access to premium
            features — premium features require a Breath Lab Premium
            subscription during and after the beta period. We do not guarantee
            uninterrupted availability of the Service during beta; the
            limitations of liability and disclaimers in Sections 9 and 10 apply
            throughout.
          </p>
        </section>

        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>7. Intellectual Property</h2>
          <p>
            All content in Breath Lab, including but not limited to text,
            graphics, audio files, and software, is the property of Sequence
            Studios or its licensors and is protected by applicable intellectual
            property laws. You may not reproduce, distribute, or create
            derivative works from any content without our prior written consent.
          </p>
        </section>

        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>8. Prohibited Conduct</h2>
          <p>You agree not to:</p>
          <ul className={styles.list}>
            <li>Use the Service for any unlawful purpose</li>
            <li>
              Attempt to gain unauthorised access to any part of the Service or
              its infrastructure
            </li>
            <li>
              Reverse engineer, decompile, or disassemble any part of the
              Service
            </li>
            <li>
              Use the Service to transmit harmful, offensive, or disruptive
              content
            </li>
            <li>
              Impersonate any person or entity or misrepresent your affiliation
            </li>
            <li>
              Resell or commercially exploit the Service without our written
              consent
            </li>
          </ul>
        </section>

        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>9. Limitation of Liability</h2>
          <p>
            To the fullest extent permitted by applicable law, Sequence Studios
            shall not be liable for any indirect, incidental, special,
            consequential, or punitive damages arising from your use of, or
            inability to use, the Service — including but not limited to
            physical injury, illness, or harm arising from the practice of any
            breathing technique described in or accessed through the Service.
          </p>
          <p>
            Our total aggregate liability for any claim arising from these Terms
            or the Service shall not exceed the greater of: (a) the amount you
            paid us in the 12 months preceding the claim, or (b) £10 where you
            have not paid us anything in that period.
          </p>
          <p>
            Nothing in these Terms excludes or limits liability for death or
            personal injury caused by our negligence, fraud or fraudulent
            misrepresentation, or any other liability that cannot be excluded or
            limited under English law.
          </p>
        </section>

        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>10. Disclaimer of Warranties</h2>
          <p>
            The Service is provided &ldquo;as is&rdquo; and &ldquo;as
            available&rdquo; without warranties of any kind, express or implied,
            including but not limited to warranties of merchantability, fitness
            for a particular purpose, or non-infringement. We do not warrant
            that the Service will be uninterrupted, error-free, or free of
            viruses or other harmful components.
          </p>
          <p>
            Nothing in this section affects your statutory rights as a consumer
            under the Consumer Rights Act 2015 or any other applicable consumer
            protection legislation. Where the law implies terms that cannot be
            excluded, those terms apply to the fullest extent permitted.
          </p>
        </section>

        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>11. Indemnification</h2>
          <p>
            You agree to indemnify and hold harmless Sequence Studios and its
            officers, directors, employees, and agents from and against any
            claims, damages, losses, liabilities, costs, and expenses (including
            reasonably incurred legal fees) arising directly out of: (a) your
            material breach of these Terms; or (b) your wilful or negligent
            violation of any third-party rights in connection with your use of
            the Service. This indemnity does not apply to the extent that a
            claim arises from our own negligence, breach of contract, or breach
            of statutory duty.
          </p>
        </section>

        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>12. Termination by Us</h2>
          <p>We may suspend or terminate your access to the Service for the following reasons:</p>
          <ul className={styles.list}>
            <li>
              <strong>Material breach:</strong> If you materially breach these
              Terms and fail to remedy the breach within 14 days of written
              notice from us (where the breach is capable of remedy), we may
              terminate your account.
            </li>
            <li>
              <strong>Fraud or abuse:</strong> If we have reasonable grounds to
              believe your account is being used fraudulently, to harm others,
              or in a way that poses a security risk to the Service or other
              users, we may suspend or terminate your account immediately
              without prior notice.
            </li>
            <li>
              <strong>Legal obligation:</strong> If we are required to do so by
              law or a regulatory authority.
            </li>
          </ul>
          <p>
            Where we terminate under material breach or legal obligation, we
            will provide at least 14 days&rsquo; written notice by email. Where
            we terminate for fraud or abuse, notice may be given at or after the
            time of suspension or termination.
          </p>
          <p>
            If we terminate your account for reasons other than your breach or
            fraud, we will refund any unused pro-rata portion of your current
            paid subscription period. No refund will be issued where termination
            is due to your material breach of these Terms.
          </p>
          <p>
            Your account data will be handled in accordance with our Privacy
            Policy following termination. If you believe a suspension or
            termination was made in error, please contact us at{" "}
            <a href="mailto:info@sequence-studios.com">
              info@sequence-studios.com
            </a>
            .
          </p>
        </section>

        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>13. Account Termination by You</h2>
          <p>
            You may delete your account at any time from the account settings
            within the app or by contacting us at{" "}
            <a href="mailto:info@sequence-studios.com">
              info@sequence-studios.com
            </a>
            . On deletion, your personal data will be handled in accordance
            with our Privacy Policy. If you have an active paid subscription,
            deleting your account does not automatically cancel it — you must
            cancel your subscription separately through the relevant platform
            (Apple App Store or Google Play for mobile; the Profile page for
            web) before deleting your account to avoid further charges.
          </p>
        </section>

        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>14. Changes to Terms</h2>
          <p>
            We reserve the right to modify these Terms at any time. For
            material changes — changes that affect your rights or obligations in
            a significant way — we will notify active subscribers by email or
            in-app notification before they take effect. For all other users,
            and for non-material changes, we will update the &ldquo;Last
            updated&rdquo; date above without further notice. Continued use of
            the Service after changes take effect constitutes your acceptance of
            the updated Terms.
          </p>
        </section>

        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>
            15. Governing Law and Disputes
          </h2>
          <p>
            These Terms are governed by and construed in accordance with the
            laws of England and Wales. Any dispute arising out of or in
            connection with these Terms or the Service shall be subject to the
            exclusive jurisdiction of the courts of England and Wales, except
            that either party may seek interim or emergency injunctive relief in
            any court of competent jurisdiction. Nothing in this clause affects
            the mandatory consumer protection laws applicable in your country of
            residence if you are a consumer in the European Economic Area.
          </p>
          <p>
            Before initiating formal proceedings, we encourage you to contact us
            at{" "}
            <a href="mailto:info@sequence-studios.com">
              info@sequence-studios.com
            </a>{" "}
            to attempt to resolve any dispute informally.
          </p>
        </section>

        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>16. Severability</h2>
          <p>
            If any provision of these Terms is found to be invalid, unlawful, or
            unenforceable by a court of competent jurisdiction, that provision
            shall be modified to the minimum extent necessary to make it
            enforceable, or severed if modification is not possible. The
            remaining provisions shall continue in full force and effect.
          </p>
        </section>

        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>17. Entire Agreement</h2>
          <p>
            These Terms, together with our Privacy Policy, constitute the entire
            agreement between you and Sequence Studios with respect to the
            Service and supersede all prior agreements, representations, and
            understandings.
          </p>
        </section>

        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>18. No Waiver</h2>
          <p>
            Our failure or delay in enforcing any right or provision of these
            Terms shall not constitute a waiver of that right or provision. A
            waiver of any right is only effective if made in writing and signed
            by an authorised representative of Sequence Studios.
          </p>
        </section>

        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>19. Assignment</h2>
          <p>
            You may not assign or transfer your rights or obligations under
            these Terms without our prior written consent. We may assign our
            rights and obligations under these Terms to any successor entity in
            connection with a merger, acquisition, or sale of all or
            substantially all of our assets, provided that the successor agrees
            to be bound by these Terms. We will notify you of any such
            assignment by email or in-app notification.
          </p>
        </section>

        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>20. Contact Us</h2>
          <p>
            If you have any questions about these Terms, please contact us at:
          </p>
          <p className={styles.contactInfo}>
            Sequence Studios Ltd (Company No. 15455683)
            <br />
            71-75 Shelton Street, Covent Garden, London WC2H 9JQ
            <br />
            United Kingdom
            <br />
            <a href="mailto:info@sequence-studios.com">
              info@sequence-studios.com
            </a>
          </p>
        </section>
      </div>
    </div>
  );
}
